import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import _ from 'lodash';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { useState } from 'react';
import useSupabaseAuth from './useSupabaseAuth';

const schema = z.object({
	email: z.string().email('Enter a valid email').nonempty('Email is required'),
	password: z.string().min(1, 'Password is required')
});

type FormType = z.infer<typeof schema>;

const defaultValues: FormType = {
	email: '',
	password: ''
};

function SupabaseSignInForm() {
	const { signIn } = useSupabaseAuth();
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { control, formState, handleSubmit } = useForm<FormType>({
		mode: 'onChange',
		defaultValues,
		resolver: zodResolver(schema)
	});

	const { isValid, dirtyFields } = formState;

	async function onSubmit(formData: FormType) {
		setErrorMessage(null);

		try {
			await signIn(formData);
		} catch (error: unknown) {
			const message =
				error instanceof Error ? error.message : 'Sign in failed. Please try again.';
			setErrorMessage(message);
		}
	}

	return (
		<form
			name="loginForm"
			noValidate
			className="flex w-full flex-col justify-center"
			onSubmit={handleSubmit(onSubmit)}
		>
			{errorMessage && (
				<Alert
					severity="error"
					className="mb-6"
					onClose={() => setErrorMessage(null)}
				>
					{errorMessage}
				</Alert>
			)}

			<Controller
				name="email"
				control={control}
				render={({ field, fieldState }) => (
					<TextField
						{...field}
						className="mb-6"
						label="Email"
						autoFocus
						type="email"
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						variant="outlined"
						required
						fullWidth
					/>
				)}
			/>

			<Controller
				name="password"
				control={control}
				render={({ field, fieldState }) => (
					<TextField
						{...field}
						className="mb-6"
						label="Password"
						type="password"
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						variant="outlined"
						required
						fullWidth
					/>
				)}
			/>

			<Button
				variant="contained"
				color="secondary"
				className="mt-4 w-full"
				aria-label="Sign in"
				disabled={_.isEmpty(dirtyFields) || !isValid}
				type="submit"
				size="large"
			>
				Sign in
			</Button>
		</form>
	);
}

export default SupabaseSignInForm;
